import { useState } from "react";
import {
  FormDataConsumer,
  ReferenceInput,
  required,
  SelectInput,
  SimpleForm,
  TextInput,
  ToolbarProps,
  useRecordContext,
  useTranslate,
} from "react-admin";

import { LocationTypeEnum } from "../generated/graphql";
import { useOrganizationContext } from "../hooks/useOrganizationContext";

export type LocationFormData = {
  name: string;
  externalId: string;
  type?: LocationTypeEnum;
  partOfId?: string;
};

type LocationFormProps = {
  toolbar?: React.ReactElement<ToolbarProps>;
};

export const LocationForm = ({ toolbar }: LocationFormProps) => {
  const record = useRecordContext();
  const translate = useTranslate();
  const { organizationId: selectedOrganizationId } = useOrganizationContext();

  // For edit mode, use the record's organizationId, for create mode use the selected organization
  const organizationId = record?.organizationId || selectedOrganizationId;

  // State to track the current location type
  const [type, setType] = useState<LocationTypeEnum | undefined>(record?.type);

  // Define the hierarchical rules: what type can be a parent of what type
  // Hierarchy: SITE > DEPARTMENT > WARD > ROOM > BED
  // Note: DEPARTMENT is optional, so WARD can have SITE or DEPARTMENT as parent
  const getValidParentTypes = (currentType: LocationTypeEnum): LocationTypeEnum[] => {
    switch (currentType) {
      case LocationTypeEnum.Department:
        return [LocationTypeEnum.Site];
      case LocationTypeEnum.Ward:
        return [LocationTypeEnum.Site, LocationTypeEnum.Department];
      case LocationTypeEnum.Room:
        return [LocationTypeEnum.Ward];
      case LocationTypeEnum.Bed:
        return [LocationTypeEnum.Room];
      case LocationTypeEnum.Organization:
      case LocationTypeEnum.Site:
      default:
        return []; // SITE is top-level and cannot have parents
    }
  };

  // Get the valid parent types for the current location type
  const validParentTypes = type ? getValidParentTypes(type) : [];

  // Create the filter for parent locations
  const parentLocationFilter: Record<string, unknown> = { organizationId };
  if (validParentTypes.length > 0) {
    parentLocationFilter.type = validParentTypes;
  }

  const typeChoices = [
    { id: LocationTypeEnum.Site, name: translate("locationType.SITE") },
    { id: LocationTypeEnum.Department, name: translate("locationType.DEPARTMENT") },
    { id: LocationTypeEnum.Ward, name: translate("locationType.WARD") },
    { id: LocationTypeEnum.Room, name: translate("locationType.ROOM") },
    { id: LocationTypeEnum.Bed, name: translate("locationType.BED") },
  ];

  if (record?.type === LocationTypeEnum.Organization) return null;

  return (
    <SimpleForm toolbar={toolbar}>
      <TextInput source="name" label={translate("locationName")} validate={required()} fullWidth />
      <SelectInput
        source="type"
        label={translate("type")}
        choices={typeChoices}
        fullWidth
        onChange={value => setType(value)}
        validate={required()}
        disabled={!!record?.id}
        helperText={record?.id ? translate("locationTypeImmutable") : undefined}
      />
      <FormDataConsumer>
        {({ formData, ...rest }) => {
          const currentType = formData.type || type;
          const currentValidParentTypes = currentType ? getValidParentTypes(currentType) : [];

          const currentParentLocationFilter: Record<string, unknown> = {
            organizationId,
          };
          if (currentValidParentTypes.length > 0) {
            currentParentLocationFilter.type = currentValidParentTypes;
          }
          // Exclude the current location from being its own parent
          if (record?.id) {
            currentParentLocationFilter.excludeId = record.id;
          }

          return (
            <ReferenceInput
              source="partOfId"
              reference="Location"
              filter={currentParentLocationFilter}
              key={currentType} // Force re-render when location type changes
              {...rest}
            >
              <SelectInput
                label={translate("parentLocation")}
                optionText="name"
                fullWidth
                disabled={currentValidParentTypes.length === 0}
                helperText={
                  currentValidParentTypes.length === 0
                    ? translate("sitesCannotHaveParent")
                    : currentType && currentValidParentTypes.length > 0
                      ? `${translate("canOnlyBelongToTypes")} ${currentValidParentTypes
                          .map(type => {
                            switch (type) {
                              case LocationTypeEnum.Site:
                                return translate("locationType.SITE");
                              case LocationTypeEnum.Department:
                                return translate("locationType.DEPARTMENT");
                              case LocationTypeEnum.Ward:
                                return translate("locationType.WARD");
                              case LocationTypeEnum.Room:
                                return translate("locationType.ROOM");
                              case LocationTypeEnum.Bed:
                                return translate("locationType.BED");
                              default:
                                return type;
                            }
                          })
                          .join(", ")}`
                      : undefined
                }
              />
            </ReferenceInput>
          );
        }}
      </FormDataConsumer>
    </SimpleForm>
  );
};
